/* AgentMeeting — Replay & Fork
 *
 * Wired to the live backend:
 *   - reads localStorage `agentmeeting.currentMeetingId` to pick the active meeting
 *   - GETs meeting messages + forks list on mount (parallel)
 *   - "Fork from here" calls POST /forks, then redirects into the new room
 *   - Falls back to a "no meeting selected" empty state when no id is present
 *
 * Visual layout (branch graph, transcript scrubber, 4-pane compare grid) is
 * preserved from the wave-3 mock; mutation panel + rewind/re-run are still
 * visual-only stubs (Wave 4 backlog).
 */
import React from 'react';
import { AGENDA, AgentT, STANCE_COLOR } from '../shared/data';
import { AsciiSep, Avatar, I, ModelChip, StanceTag, renderInline } from '../shared/ui';
import { meetings as meetingsApi, forks as forksApi, HTTPError } from '../shared/api';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

type ReplayProps = {
  embedded?: boolean;
  topBar?: React.ReactNode;
};

interface ServerMsg {
  id: string;
  round_no: number;
  participant_id: string;
  kind: string;
  content: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_cents?: number;
  parent_id?: string | null;
  created_at?: string;
  speaker_name?: string;
  speaker_short?: string;
  speaker_stance?: string;
  speaker_model?: string;
}

interface ForkRow {
  id: string;
  title?: string;
  status?: string;
  fork_from_message_id?: string;
  created_at?: string;
  parent_id?: string;
  // verdict / msg counts may not exist server-side yet
  verdict?: string;
  msgs?: number;
}

function synthAgent(m: ServerMsg | undefined): AgentT {
  if (!m) {
    return {
      id: '—', name: '—', short: '··',
      stance: 'neutral' as AgentT['stance'], stanceLabel: '',
      model: null as AgentT['model'], persona: '',
    };
  }
  const stance = (m.speaker_stance || 'neutral') as AgentT['stance'];
  return {
    id: m.participant_id || m.id,
    name: m.speaker_name || '—',
    short: m.speaker_short || '··',
    stance,
    stanceLabel: '',
    model: (m.speaker_model || null) as AgentT['model'],
    persona: '',
  };
}

function fmtTs(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
  } catch { return ''; }
}

function goMeetings() {
  if (typeof window !== 'undefined') location.hash = '#/app/meetings';
}

const BRANCH_PALETTE = [
  'var(--live)', 'var(--warn)', 'var(--pro)', 'var(--err)',
  'var(--neutral)', 'var(--judge)', 'var(--risk)', 'var(--finance)',
];

export function Replay({ embedded, topBar }: ReplayProps) {
  useAuth();
  const { t } = useI18n();

  const meetingId = typeof window !== 'undefined'
    ? localStorage.getItem('agentmeeting.currentMeetingId')
    : null;

  const [messages, setMessages] = React.useState<ServerMsg[] | null>(null);
  const [forkList, setForkList] = React.useState<ForkRow[] | null>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [scrub, setScrub] = React.useState(0);
  const [forkErr, setForkErr] = React.useState<string | null>(null);
  const [forking, setForking] = React.useState(false);

  React.useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    Promise.all([
      meetingsApi.messages(meetingId)
        .then(r => (r.messages || []) as ServerMsg[])
        .catch((e: unknown) => {
          if (!cancelled) setLoadErr(e instanceof HTTPError ? `messages ${e.status}` : `${e}`);
          return [] as ServerMsg[];
        }),
      forksApi.list(meetingId)
        .then(r => (r.forks || []) as ForkRow[])
        .catch((e: unknown) => {
          if (!cancelled) setLoadErr(e instanceof HTTPError ? `forks ${e.status}` : `${e}`);
          return [] as ForkRow[];
        }),
    ]).then(([msgs, fks]) => {
      if (cancelled) return;
      setMessages(msgs);
      setForkList(fks);
      // Park cursor at the most recent message by default.
      if (msgs.length > 0) setScrub(msgs.length - 1);
    });
    return () => { cancelled = true; };
  }, [meetingId]);

  // ── Empty state ────────────────────────────────────────────
  if (!meetingId) {
    return (
      <div className="frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="col gap-3" style={{
          maxWidth: 420, padding: 24, border: '1px solid var(--line-2)',
          borderRadius: 6, background: 'var(--bg-1)', textAlign: 'center',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NO MEETING SELECTED</div>
          <div style={{ fontSize: 15 }}>Pick a meeting to replay.</div>
          <button className="btn primary" onClick={goMeetings}>Open meetings →</button>
        </div>
      </div>
    );
  }

  // ── Loading state ──────────────────────────────────────────
  if (messages == null || forkList == null) {
    return (
      <div className="frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="mono tiny dim">loading replay…</div>
        {loadErr && <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{loadErr}</div>}
      </div>
    );
  }

  const safeScrub = Math.max(0, Math.min(scrub, Math.max(0, messages.length - 1)));
  const cursorMsg = messages[safeScrub];

  // Synthesize "branches" for tab strip — `main` plus every server fork.
  const branches: Array<{
    id: string;
    label: string;
    color: string;
    msgs: number;
    status: string;
    verdict: string;
    fork_from_message_id?: string;
  }> = [
    {
      id: meetingId,
      label: 'main',
      color: BRANCH_PALETTE[0],
      msgs: messages.length,
      status: 'live',
      verdict: 'pending',
    },
    ...forkList.map((f, i) => ({
      id: f.id,
      label: f.title || `fork-${i + 1}`,
      color: BRANCH_PALETTE[(i + 1) % BRANCH_PALETTE.length],
      msgs: f.msgs ?? 0,
      status: f.status || 'pending',
      verdict: f.verdict || 'pending',
      fork_from_message_id: f.fork_from_message_id,
    })),
  ];

  const onForkFromHere = async (msg: ServerMsg) => {
    if (!msg || !meetingId) return;
    setForkErr(null);
    setForking(true);
    try {
      const res = await forksApi.create(meetingId, { from_message_id: msg.id });
      try { localStorage.setItem('agentmeeting.currentMeetingId', res.id); } catch { /* noop */ }
      if (typeof window !== 'undefined') location.hash = '#/app/room';
    } catch (e: unknown) {
      setForkErr(e instanceof HTTPError ? `fork ${e.status}` : `${e}`);
      setForking(false);
    }
  };

  // Group messages by round_no for the transcript pane.
  const byRound = new Map<number, ServerMsg[]>();
  for (const m of messages) {
    const arr = byRound.get(m.round_no) || [];
    arr.push(m);
    byRound.set(m.round_no, arr);
  }
  const rounds = Array.from(byRound.keys()).sort((a, b) => a - b);
  const cursorRoundNo = cursorMsg?.round_no ?? 0;

  return (
    <div className="frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (
        <div className="term-bar">
          <div className="lights"><span/><span/><span/></div>
          <div className="crumbs">
            <span>~/meetings/{meetingId.slice(0, 8)}</span>
            <span className="sep">/</span>
            <span className="here">replay & fork</span>
          </div>
          <div className="right">
            <span className="mono tiny dim">
              cursor → {cursorMsg ? `${cursorMsg.id.slice(0, 6)} · r${cursorRoundNo + 1}` : '—'}
            </span>
            <button className="btn"
              disabled={!cursorMsg || forking}
              onClick={() => cursorMsg && onForkFromHere(cursorMsg)}>
              {I.fork} {t('app.replay.fork')}
            </button>
          </div>
        </div>
      )}

      <div className="grow flex col" style={{ minHeight: 0 }}>
        {/* branch tabs */}
        <div className="flex center gap-1 wrap" style={{
          padding: '10px 16px', borderBottom: '1px solid var(--line-1)', background: 'var(--bg-1)',
        }}>
          <span className="mono tiny dim" style={{ marginRight: 8, letterSpacing: '0.08em' }}>{t('app.replay.branch')}</span>
          {branches.map((b, i) => (
            <div key={b.id} className="flex center gap-2" style={{
              padding: '5px 10px',
              border: '1px solid ' + (i === 0 ? b.color : 'var(--line-2)'),
              background: i === 0 ? 'var(--bg-3)' : 'var(--bg-2)',
              borderRadius: 4,
              fontFamily: 'var(--mono)', fontSize: 11,
            }}>
              <span className="dot" style={{ background: b.color }} />
              <span>{b.label}</span>
              <span className="dim">· {b.msgs}</span>
              <span style={{
                fontSize: 9, padding: '1px 4px', borderRadius: 2,
                background: b.verdict === 'approve' ? 'var(--live-dim)' :
                            b.verdict === 'block' ? 'rgba(248,113,113,0.15)' : 'var(--bg-3)',
                color: b.verdict === 'approve' ? 'var(--live)' :
                       b.verdict === 'block' ? 'var(--err)' : 'var(--fg-3)',
                letterSpacing: '0.04em',
              }}>{(b.status || b.verdict).toUpperCase()}</span>
            </div>
          ))}
          <button className="btn ghost icon" style={{ marginLeft: 4 }}
            disabled={!cursorMsg || forking}
            title="fork from cursor"
            onClick={() => cursorMsg && onForkFromHere(cursorMsg)}>
            {I.plus}
          </button>
          {forkErr && <span className="mono tiny" style={{ color: 'var(--err)', marginLeft: 8 }}>{forkErr}</span>}
        </div>

        <div className="grow flex" style={{ minHeight: 0 }}>
          {/* left: branch tree (graph) */}
          <div className="col" style={{
            width: 260, flex: '0 0 260px',
            borderRight: '1px solid var(--line-1)', background: 'var(--bg-1)',
          }}>
            <div className="flex center between" style={{
              padding: '12px 14px', borderBottom: '1px solid var(--line-1)',
            }}>
              <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.replay.branchGraph')}</span>
              <span className="mono tiny dim">{forkList.length} fork{forkList.length === 1 ? '' : 's'}</span>
            </div>
            <BranchGraph
              messages={messages}
              forks={forkList}
              cursor={safeScrub}
              palette={BRANCH_PALETTE}
            />
          </div>

          {/* center: transcript w/ cursor */}
          <div className="col grow" style={{ minWidth: 0 }}>
            <div className="grow scroll col" style={{ padding: '10px 0' }}>
              {messages.length === 0 && (
                <div className="meta dim" style={{ padding: 32, textAlign: 'center' }}>
                  (no messages yet on this meeting)
                </div>
              )}
              {rounds.map(rno => {
                const agendaEntry = AGENDA[rno];
                const label = agendaEntry
                  ? `R${rno + 1} · ${agendaEntry.label}`
                  : `R${rno + 1}`;
                return (
                  <React.Fragment key={rno}>
                    <AsciiSep label={label} total={70} />
                    {(byRound.get(rno) || []).map(m => {
                      const mIdx = messages.findIndex(x => x.id === m.id);
                      const isPast = mIdx < safeScrub;
                      const isCursor = mIdx === safeScrub;
                      const isFuture = mIdx > safeScrub;
                      return (
                        <ReplayRow
                          key={m.id}
                          msg={m}
                          isPast={isPast}
                          isCursor={isCursor}
                          isFuture={isFuture}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {messages.length > 0 && safeScrub < messages.length - 1 && (
                <div style={{ padding: '12px 22px', opacity: 0.4 }}>
                  <div className="mono tiny dim">
                    ▼ unplayed · {messages.length - safeScrub - 1} more turn(s)
                  </div>
                </div>
              )}
            </div>

            {/* fork inspector docked at bottom of transcript */}
            {cursorMsg && (
              <ForkInspector
                msg={cursorMsg}
                forking={forking}
                onFork={() => onForkFromHere(cursorMsg)}
              />
            )}
          </div>

          {/* right: fork mutation panel — visual stub (Wave 4 backlog) */}
          <div className="col" style={{
            width: 320, flex: '0 0 320px',
            borderLeft: '1px solid var(--line-1)', background: 'var(--bg-1)',
          }}>
            <div className="flex center between" style={{
              padding: '12px 14px', borderBottom: '1px solid var(--line-1)',
            }}>
              <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.replay.mutations')}</span>
              <span className="mono tiny dim">wave 4 backlog</span>
            </div>
            <div className="col grow scroll gap-3" style={{ padding: 14 }}>
              <Mutation kind="agent" label="modify agent · system prompt" body={
                <div className="col gap-2 sm">
                  <div className="meta dim">替换 system prompt — 重跑后看立场如何变化 (mutations not yet wired)</div>
                  <textarea className="input mono" rows={4} style={{ fontSize: 10.5 }}
                    placeholder="(coming soon — wave 4)" disabled />
                </div>
              } />
              <Mutation kind="message" label="rewrite message · cursor" body={
                <div className="col gap-2 sm">
                  <div className="meta dim">改 cursor 处的消息, 看后续讨论如何分叉</div>
                  <textarea className="input mono" rows={3} style={{ fontSize: 10.5 }}
                    placeholder="(coming soon — wave 4)" disabled />
                </div>
              } />
              <Mutation kind="rule" label="change scheduling" body={
                <div className="col gap-2 sm">
                  <div className="meta dim">把 reactive 换成 free-form (claim-token), 看 agent 抢麦排序</div>
                </div>
              } />

              <button className="btn primary" disabled
                title="re-run from cursor — coming next wave"
                style={{ marginTop: 'auto' }}>
                {I.play} re-run from cursor
              </button>
              <div className="meta dim" style={{ textAlign: 'center' }}>
                preview · stub only — Wave 4 backlog
              </div>
            </div>
          </div>
        </div>

        {/* compare grid (downsized to N=forks.length panes if needed) */}
        {forkList.length > 0 && (
          <CompareGrid forks={forkList} palette={BRANCH_PALETTE} />
        )}

        {/* timeline scrubber */}
        <Scrubber messages={messages} scrub={safeScrub} setScrub={setScrub} />
      </div>
    </div>
  );
}

function ReplayRow({ msg, isPast, isCursor, isFuture }: {
  msg: ServerMsg; isPast: boolean; isCursor: boolean; isFuture: boolean;
}) {
  const a = synthAgent(msg);
  return (
    <div className="flex gap-3" style={{
      padding: '10px 22px',
      opacity: isFuture ? 0.32 : 1,
      borderLeft: isCursor ? '2px solid var(--live)' : '2px solid transparent',
      background: isCursor ? 'var(--bg-1)' : 'transparent',
    }}>
      <Avatar agent={a} size="sm" />
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div className="flex center gap-2 wrap">
          <span className="sm" style={{ fontWeight: 500 }}>{a.name}</span>
          <StanceTag agent={a} />
          <span className="dim">·</span>
          {a.model && (
            <>
              <ModelChip modelId={a.model} withCost costCents={msg.cost_cents || 0} />
              <span className="dim">·</span>
            </>
          )}
          <span className="mono tiny dim">{msg.id.slice(0, 6)}</span>
          {isCursor && <span className="mono tiny live-fg" style={{ marginLeft: 'auto' }}>◀ CURSOR</span>}
          {isPast && !isCursor && <span className="mono tiny dim" style={{ marginLeft: 'auto' }}>played</span>}
          {isFuture && <span className="mono tiny dim" style={{ marginLeft: 'auto' }}>future</span>}
        </div>
        <div className="md" style={{ lineHeight: 1.55, color: 'var(--fg-1)' }}>
          {renderInline(msg.content || '')}
        </div>
      </div>
    </div>
  );
}

function ForkInspector({ msg, forking, onFork }: {
  msg: ServerMsg; forking: boolean; onFork: () => void;
}) {
  const { t } = useI18n();
  const a = synthAgent(msg);
  return (
    <div className="col" style={{
      borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
    }}>
      <div className="flex center" style={{
        padding: '10px 16px', gap: 12, borderBottom: '1px solid var(--line-1)',
      }}>
        <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.replay.cursor.inspect')}</span>
        <span className="mono sm">{msg.id.slice(0, 8)} · r{msg.round_no + 1}</span>
        <Avatar agent={a} size="sm" />
        <span className="sm">{a.name}</span>
        {a.model && <ModelChip modelId={a.model} withCost costCents={msg.cost_cents || 0} />}
        <span style={{ marginLeft: 'auto' }} className="flex gap-2">
          <button className="btn ghost" disabled title="rewind — Wave 4 backlog">
            {I.rewind} {t('app.replay.rewindHere')}
          </button>
          <button className="btn" disabled={forking} onClick={onFork}>
            {I.fork} {forking ? 'forking…' : t('app.replay.forkHere')}
          </button>
        </span>
      </div>
      <div className="flex" style={{ padding: '10px 16px', gap: 24 }}>
        <Stat label="created"   value={fmtTs(msg.created_at) || '—'} />
        <Stat label="tokens in"  value={msg.tokens_in != null ? String(msg.tokens_in) : '—'} />
        <Stat label="tokens out" value={msg.tokens_out != null ? String(msg.tokens_out) : '—'} />
        <Stat label="cost"       value={msg.cost_cents != null ? `$${(msg.cost_cents/100).toFixed(2)}` : '—'} />
        <Stat label="parent"     value={msg.parent_id ? msg.parent_id.slice(0, 8) : '—'} />
        <Stat label="kind"       value={msg.kind || 'speech'} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="col">
      <div className="mono tiny dim" style={{ letterSpacing: '0.06em' }}>{label.toUpperCase()}</div>
      <div className="mono sm tnum">{value}</div>
    </div>
  );
}

function Mutation({ kind, label, body }: {
  kind: 'agent' | 'message' | 'participant' | 'rule';
  label: string;
  body: React.ReactNode;
}) {
  return (
    <div className="col gap-2" style={{
      padding: 12, border: '1px solid var(--line-2)', borderRadius: 4, background: 'var(--bg-2)',
    }}>
      <div className="flex center between">
        <div className="flex center gap-2">
          <span className="tag" style={{
            color: kind === 'agent' ? 'var(--neutral)' :
                   kind === 'message' ? 'var(--warn)' :
                   kind === 'participant' ? 'var(--live)' : 'var(--pro)',
            borderColor: 'currentColor',
          }}>{kind}</span>
          <span className="sm" style={{ fontWeight: 500 }}>{label}</span>
        </div>
        <button className="btn ghost icon" disabled>{I.x}</button>
      </div>
      {body}
    </div>
  );
}

function BranchGraph({ messages, forks, cursor, palette }: {
  messages: ServerMsg[];
  forks: ForkRow[];
  cursor: number;
  palette: string[];
}) {
  const W = 260, H = Math.max(160, 80 + Math.max(forks.length, 1) * 60);
  const mainY = 60;
  const leftPad = 30;
  const rightPad = 16;
  const usable = W - leftPad - rightPad;
  const denom = Math.max(1, messages.length - 1);
  const xFor = (i: number) => leftPad + (denom === 0 ? 0 : (i / denom) * usable);

  // For each fork, find x of the parent message on main.
  const forkLanes = forks.map((f, i) => {
    const parentIdx = f.fork_from_message_id
      ? messages.findIndex(m => m.id === f.fork_from_message_id)
      : -1;
    return {
      id: f.id,
      label: f.title || `fork-${i + 1}`,
      color: palette[(i + 1) % palette.length],
      y: mainY + 60 * (i + 1),
      fromX: parentIdx >= 0 ? xFor(parentIdx) : leftPad,
    };
  });

  return (
    <div className="grow scroll" style={{ padding: 12 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        {/* main line */}
        <line x1={leftPad} y1={mainY} x2={W - rightPad} y2={mainY}
          stroke={palette[0]} strokeWidth="1.2" strokeOpacity="0.5" />
        {messages.map((m, i) => (
          <circle key={m.id} cx={xFor(i)} cy={mainY}
            r={cursor === i ? 5 : 3}
            fill={cursor === i ? 'var(--bg-0)' : palette[0]}
            stroke={palette[0]} strokeWidth={cursor === i ? 2 : 0} />
        ))}
        <text x={W - rightPad - 2} y={mainY - 8} fontFamily="var(--mono)" fontSize="9"
          fill={palette[0]} textAnchor="end" letterSpacing="0.5">main</text>

        {/* fork lanes */}
        {forkLanes.map((lane) => (
          <g key={lane.id}>
            <path d={`M ${lane.fromX} ${mainY} C ${lane.fromX + 20} ${mainY}, ${lane.fromX + 20} ${lane.y}, ${lane.fromX + 40} ${lane.y}`}
              stroke={lane.color} strokeWidth="1.2" fill="none" />
            <line x1={lane.fromX + 40} y1={lane.y} x2={W - rightPad} y2={lane.y}
              stroke={lane.color} strokeWidth="1.2" strokeOpacity="0.5" />
            <circle cx={lane.fromX + 40} cy={lane.y} r={3} fill={lane.color} />
            <text x={W - rightPad - 2} y={lane.y - 8} fontFamily="var(--mono)" fontSize="9"
              fill={lane.color} textAnchor="end" letterSpacing="0.5">{lane.label}</text>
          </g>
        ))}

        {/* cursor vertical line */}
        {messages.length > 0 && (
          <>
            <line x1={xFor(cursor)} y1={mainY - 20} x2={xFor(cursor)} y2={H - 20}
              stroke={palette[0]} strokeWidth="0.8" strokeDasharray="2 3" opacity="0.6" />
            <text x={xFor(cursor)} y={mainY - 24} fontFamily="var(--mono)" fontSize="9"
              fill={palette[0]} textAnchor="middle">CURSOR</text>
          </>
        )}
      </svg>
      <div className="meta dim" style={{ padding: '4px 0' }}>
        点 • 表示一条消息 · 弧线 = fork 分裂点 · 虚线 = 当前 cursor
      </div>
    </div>
  );
}

function CompareGrid({ forks, palette }: { forks: ForkRow[]; palette: string[] }) {
  // Show up to 4 fork preview panes.
  const slice = forks.slice(0, 4);
  if (slice.length === 0) return null;
  return (
    <div className="col" style={{
      borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
    }}>
      <div className="flex center between" style={{
        padding: '8px 16px', borderBottom: '1px solid var(--line-1)',
      }}>
        <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>
          COMPARE FORKS · {slice.length}/{forks.length}
        </span>
        <span className="mono tiny dim">verdict + msgs are wave-4 backlog</span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${slice.length}, minmax(0, 1fr))`,
        gap: 1, background: 'var(--line-1)',
      }}>
        {slice.map((f, i) => {
          const color = palette[(i + 1) % palette.length];
          return (
            <div key={f.id} className="col gap-1" style={{
              padding: 10, background: 'var(--bg-1)', minWidth: 0,
            }}>
              <div className="flex center gap-2">
                <span className="dot" style={{ background: color }} />
                <span className="mono sm" style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{f.title || `fork-${i + 1}`}</span>
              </div>
              <div className="meta dim mono tiny">
                from {f.fork_from_message_id ? f.fork_from_message_id.slice(0, 6) : '—'}
                {f.created_at && <> · {fmtTs(f.created_at)}</>}
              </div>
              <div className="flex gap-2 wrap" style={{ marginTop: 4 }}>
                <span className="tag">{(f.status || 'pending').toLowerCase()}</span>
                {f.verdict && <span className="tag">{f.verdict}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Scrubber({ messages, scrub, setScrub }: {
  messages: ServerMsg[]; scrub: number; setScrub: (n: number) => void;
}) {
  const total = messages.length;
  if (total === 0) {
    return (
      <div className="col" style={{
        padding: '10px 16px 12px', borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
      }}>
        <div className="meta dim" style={{ textAlign: 'center' }}>
          (timeline empty — no messages on this meeting yet)
        </div>
      </div>
    );
  }
  const denom = Math.max(1, total - 1);
  return (
    <div className="col" style={{
      padding: '10px 16px 12px', borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
    }}>
      <div className="flex center gap-3">
        <button className="btn icon" disabled title="play — wave 4 backlog">{I.play}</button>
        <div className="flex gap-1 grow center" style={{ height: 32, position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 0, right: 0, top: 14, height: 4,
            background: 'var(--bg-3)', borderRadius: 1,
          }} />
          <div style={{
            position: 'absolute', left: 0, top: 14, height: 4,
            width: ((scrub + 1) / total) * 100 + '%',
            background: 'var(--live)', borderRadius: 1,
          }} />
          {messages.map((m, i) => {
            const a = synthAgent(m);
            const x = (i / denom) * 100;
            return (
              <div key={m.id}
                onClick={() => setScrub(i)}
                title={`${m.id.slice(0, 6)} · ${a.name}`}
                style={{
                  position: 'absolute', left: `${x}%`, top: 9,
                  width: 14, height: 14, borderRadius: 3, transform: 'translateX(-50%)',
                  background: STANCE_COLOR[a.stance] || 'var(--fg-3)',
                  border: i === scrub ? '2px solid var(--fg-0)' : '1px solid var(--bg-0)',
                  cursor: 'pointer',
                  boxShadow: i === scrub ? '0 0 0 3px var(--live-dim)' : 'none',
                }} />
            );
          })}
        </div>
        <span className="mono tiny dim tnum">
          {String(scrub + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>
      <div className="flex center between mono tiny dim" style={{ marginTop: 6 }}>
        <span>{fmtTs(messages[0]?.created_at)}</span>
        <span>← / → 步进 · space 播放 · F fork (wave 4)</span>
        <span>{fmtTs(messages[total - 1]?.created_at)}</span>
      </div>
    </div>
  );
}
